/**
 * Pure, framework-free pieces of useCopilotChat.ts's send-message flow
 * (2026-07-18 reliability fix). Split out so this logic — and specifically
 * the fail-rolls-back / success-commits behavior — is unit-testable without
 * a DOM environment: useCopilotChat.ts itself transitively imports
 * msal-react/msalConfig, which touch `window` at module load time, and this
 * repo has no jsdom/testing-library precedent to render a hook that needs one.
 */
import { ApiError } from '@/lib/apiClient'

export type CopilotRole = 'user' | 'assistant'

export interface CopilotAction {
  tool: string
  entityType: string
  entityId: string
  summary: string | null
  link: string | null
}

/**
 * Nivel B pending action (MASTER.md AA-20): the backend deferred an
 * outward-facing operation (send mail/Teams message, event with invitees)
 * until the user explicitly confirms it. `estado` is client-side UI state:
 * 'pendiente' shows Confirmar/Cancelar; the rest are terminal.
 */
export interface CopilotPendingAction {
  id: string
  accion: string
  resumen: string
  destinatarios?: string[]
  detalles?: { fecha?: string; hora?: string } | null
  venceEnSegundos?: number
  estado?: 'pendiente' | 'ejecutada' | 'cancelada' | 'expirada' | 'error'
  resultadoTexto?: string
}

/**
 * Recibo autoritativo de escritura (integridad 2026-07-18): emitido por el
 * backend con verificación real en Graph/SharePoint — independiente del
 * texto que genere el modelo. ok:true SIEMPRE implica verified:true y un id
 * real del recurso; ok:false significa que la operación NO ocurrió.
 */
export interface CopilotExecutedAction {
  ok: boolean
  operation: string
  resourceType: string | null
  id?: string
  containerId?: string | null
  webUrl?: string | null
  verified: boolean
  errorCode?: string
  status?: number | null
  message?: string
}

export interface CopilotMessage {
  role: CopilotRole
  content: string
  actions?: CopilotAction[]
  pendingActions?: CopilotPendingAction[]
  executedActions?: CopilotExecutedAction[]
}

/**
 * Resolves what `messages` becomes after a send attempt settles. On failure
 * it returns `previousMessages` unchanged, so the attempted user turn never
 * lingers in history to be silently resent — the caller re-sending the same
 * text is then one clean turn, never a duplicate.
 */
export function nextMessagesAfterSend(
  previousMessages: CopilotMessage[],
  attemptedMessages: CopilotMessage[],
  result: { ok: true; reply: string; actions: CopilotAction[]; pendingActions?: CopilotPendingAction[]; executedActions?: CopilotExecutedAction[] } | { ok: false }
): CopilotMessage[] {
  if (!result.ok) return previousMessages
  return [...attemptedMessages, {
    role: 'assistant',
    content: result.reply,
    actions: result.actions,
    pendingActions: (result.pendingActions ?? []).map((p) => ({ ...p, estado: 'pendiente' as const })),
    executedActions: result.executedActions ?? [],
  }]
}

/**
 * Pure resolution of a confirm/cancel outcome onto the message history:
 * finds the pending action by id and stamps its terminal state so the card
 * stops offering Confirmar/Cancelar. Never removes the card — the record of
 * what was proposed and what happened stays visible.
 */
export function messagesAfterActionResolution(
  messages: CopilotMessage[],
  actionId: string,
  estado: NonNullable<CopilotPendingAction['estado']>,
  resultadoTexto?: string
): CopilotMessage[] {
  return messages.map((m) => (m.pendingActions?.some((p) => p.id === actionId)
    ? { ...m, pendingActions: m.pendingActions.map((p) => (p.id === actionId ? { ...p, estado, resultadoTexto } : p)) }
    : m))
}

export type CopilotStatus = 'idle' | 'sending' | 'not_signed_in' | 'not_configured' | 'error'

/**
 * 2026-07-18 regression fix. api/src/copilot.js can return a 503 for
 * several genuinely different reasons — the AI/SharePoint/identity
 * dependency truly isn't configured, an Anthropic/Graph/SharePoint call
 * timed out (ANTHROPIC_TIMEOUT/GRAPH_TIMEOUT/SHAREPOINT_TIMEOUT), the total
 * request time budget ran out (COPILOT_TIME_BUDGET_EXCEEDED), or Anthropic
 * itself returned an upstream error (AI_UPSTREAM_ERROR). Only the codes in
 * NOT_CONFIGURED_ERROR_CODES mean "this deployment genuinely lacks a
 * dependency" — everything else 503-shaped is a transient failure and must
 * resolve to 'error', the only status whose UI shows the correlationId
 * (CopilotChat.tsx). Before this fix every 503 resolved to 'not_configured',
 * which silently hid the correlationId and showed a flatly incorrect
 * "IA no configurada" message for timeouts and upstream failures alike.
 */
export const NOT_CONFIGURED_ERROR_CODES = new Set(['AI_NOT_CONFIGURED', 'SHAREPOINT_NOT_CONFIGURED', 'IDENTITY_NOT_CONFIGURED'])

export function statusFromError(err: unknown): CopilotStatus {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'not_signed_in'
    if (err.status === 503 && NOT_CONFIGURED_ERROR_CODES.has(err.message)) return 'not_configured'
  }
  return 'error'
}

/**
 * 2026-07-18 follow-up (production regression still reproduces after the
 * classification fix above, now correctly showing status 'error' + a
 * correlationId but only a generic "no pude enviar tu mensaje"). Azure
 * Static Web Apps Free tier has no diagnostic-settings/Log Analytics
 * attached (confirmed via `az monitor diagnostic-settings list` returning
 * `[]`) and Application Insights is out of scope, so the correlationId
 * alone can't be looked up anywhere — the backend's own error `code` is the
 * only signal available, and it's already right there on the ApiError
 * (api/src/copilot.js's JSON body always has `{error: 'CODE', ...}`, and
 * src/lib/apiClient.ts's toApiError puts that CODE in `message`). This maps
 * each *category* of failure to a plain-language explanation without ever
 * showing the raw code or any upstream detail.
 *
 * Note: GRAPH_TIMEOUT/SHAREPOINT_TIMEOUT/GRAPH_UPSTREAM_ERROR/
 * SHAREPOINT_UPSTREAM_ERROR are included defensively but are NOT reachable
 * as top-level errors from copilotChatHandler today — Graph/SharePoint
 * calls made from inside a tool call are caught inside the tool loop and
 * turned into a tool_result (never a top-level HTTP error), and the one
 * SharePoint call made before the loop (authorizationService.resolveProfile)
 * swallows every error into a degraded profile rather than rethrowing. They
 * are mapped here only so a future architecture change doesn't silently
 * regress back to a generic message.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  ANTHROPIC_TIMEOUT: 'La respuesta del modelo superó el tiempo disponible.',
  AI_UPSTREAM_ERROR: 'El servicio de IA rechazó o no pudo procesar la solicitud.',
  COPILOT_TIME_BUDGET_EXCEEDED: 'Copilot necesitó más tiempo del permitido para completar el análisis.',
  GRAPH_TIMEOUT: 'Microsoft 365 tardó demasiado en responder.',
  GRAPH_UPSTREAM_ERROR: 'Microsoft 365 no pudo responder en este momento.',
  SHAREPOINT_TIMEOUT: 'Sales tardó demasiado en recuperar el contexto.',
  SHAREPOINT_UPSTREAM_ERROR: 'Sales no pudo recuperar el contexto en este momento.',
  RATE_LIMITED: 'Estás enviando mensajes muy rápido — espera un momento e inténtalo de nuevo.',
  PROFILE_NOT_ACTIVE: 'Tu perfil no está activo en Sales en este momento.',
}

const GENERIC_ERROR_MESSAGE = 'No pude enviar tu mensaje. Puedes intentarlo de nuevo.'

/**
 * User-facing message for the 'error' status — never the raw backend code
 * or any upstream detail, just a plain-language category (when the code is
 * recognized) or a generic fallback, plus the correlationId when the
 * backend provided one.
 */
export function friendlyErrorMessage(error: ApiError | Error): string {
  const base = (error instanceof ApiError && ERROR_CODE_MESSAGES[error.message]) || GENERIC_ERROR_MESSAGE
  if (error instanceof ApiError && error.correlationId) {
    return `${base} Código de referencia: ${error.correlationId}`
  }
  return base
}
